import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import PharmeasyLayout from "@/components/PharmeasyLayout";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Search, RefreshCw, Loader2, AlertTriangle, CheckCircle2,
  Clock, XCircle, RotateCcw, ChevronLeft, ChevronRight,
  FileSpreadsheet, Eye,
} from "lucide-react";

const STATUS_CONFIG = {
  open: { label: "Open", color: "bg-blue-100 text-blue-700 border-blue-200", icon: AlertTriangle },
  in_progress: { label: "In Progress", color: "bg-amber-100 text-amber-700 border-amber-200", icon: Clock },
  resolved: { label: "Resolved", color: "bg-green-100 text-green-700 border-green-200", icon: CheckCircle2 },
  closed: { label: "Closed", color: "bg-slate-100 text-slate-600 border-slate-200", icon: XCircle },
  reopened: { label: "Reopened", color: "bg-red-100 text-red-700 border-red-200", icon: RotateCcw },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.open;
  const Icon = cfg.icon;
  return (
    <Badge data-testid={`status-badge-${status}`} variant="outline" className={`${cfg.color} border gap-1 text-xs font-medium`}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </Badge>
  );
};

const StatsCards = ({ stats }) => {
  if (!stats) return null;
  const cards = [
    { label: "Total", value: stats.total || 0, color: "text-slate-700", bg: "bg-white" },
    { label: "Open", value: stats.by_status?.open || 0, color: "text-blue-700", bg: "bg-blue-50" },
    { label: "In Progress", value: stats.by_status?.in_progress || 0, color: "text-amber-700", bg: "bg-amber-50" },
    { label: "Resolved", value: stats.by_status?.resolved || 0, color: "text-green-700", bg: "bg-green-50" },
    { label: "Closed", value: stats.by_status?.closed || 0, color: "text-slate-600", bg: "bg-slate-50" },
    { label: "Reopened", value: stats.by_status?.reopened || 0, color: "text-red-700", bg: "bg-red-50" },
  ];
  return (
    <div data-testid="escalation-stats" className="grid grid-cols-3 md:grid-cols-6 gap-3">
      {cards.map((c) => (
        <div key={c.label} className={`${c.bg} rounded-lg border p-4 text-center`}>
          <div className={`text-2xl font-bold ${c.color}`}>{c.value}</div>
          <div className="text-xs text-slate-500 mt-0.5">{c.label}</div>
        </div>
      ))}
    </div>
  );
};

const UploadSection = ({ onUploadDone }) => {
  const [uploading, setUploading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setLastResult(null);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await axios.post(`${API}/pharmeasy-escalations/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.success) {
        toast.success(`${data.total_parsed} escalations created (${data.linked_count} linked to shipments)`);
        setLastResult(data);
        onUploadDone();
      } else {
        toast.error(data.error || "Upload failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Card data-testid="upload-section">
      <CardContent className="pt-5 pb-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-teal-600" />
            <span className="text-sm font-medium text-slate-700">Upload Escalation File</span>
          </div>
          <div className="flex items-center gap-2 flex-1">
            <input
              ref={fileRef}
              data-testid="escalation-file-input"
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleUpload}
              disabled={uploading}
              className="block text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100 cursor-pointer disabled:opacity-50"
            />
            {uploading && <Loader2 className="h-4 w-4 animate-spin text-teal-600" />}
          </div>
          {lastResult && (
            <div className="text-xs text-slate-500">
              Parsed: <span className="font-medium text-slate-700">{lastResult.total_parsed}</span> | Linked: <span className="font-medium text-green-700">{lastResult.linked_count}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const EscalationManagement = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [escalations, setEscalations] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [filterValues, setFilterValues] = useState({ types: [], teams: [], suppliers: [] });
  const [selected, setSelected] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkUpdating, setBulkUpdating] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/pharmeasy-escalations/stats`);
      setStats(data);
    } catch {}
  }, []);

  const fetchFilterValues = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/pharmeasy-escalations/filter-values`);
      setFilterValues(data);
    } catch {}
  }, []);

  const fetchEscalations = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, size: 25 };
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (typeFilter && typeFilter !== "all") params.escalation_type = typeFilter;
      if (teamFilter && teamFilter !== "all") params.escalation_team = teamFilter;
      if (searchDebounced) params.search = searchDebounced;
      const { data } = await axios.get(`${API}/pharmeasy-escalations/`, { params });
      setEscalations(data.data || []);
      setTotalPages(data.total_pages || 0);
      setTotal(data.total || 0);
    } catch {
      toast.error("Failed to load escalations");
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, typeFilter, teamFilter, searchDebounced]);

  useEffect(() => { fetchStats(); fetchFilterValues(); }, [fetchStats, fetchFilterValues]);
  useEffect(() => { setPage(1); }, [statusFilter, typeFilter, teamFilter, searchDebounced]);
  useEffect(() => { fetchEscalations(); }, [fetchEscalations]);

  const refreshAll = () => {
    fetchStats();
    fetchEscalations();
    fetchFilterValues();
    setSelected(new Set());
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === escalations.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(escalations.map((e) => e.id)));
    }
  };

  const handleBulkUpdate = async () => {
    if (!bulkStatus || selected.size === 0) return;
    setBulkUpdating(true);
    try {
      const { data } = await axios.post(`${API}/pharmeasy-escalations/bulk-status`, {
        ids: Array.from(selected),
        status: bulkStatus,
      });
      toast.success(data.message);
      setBulkStatus("");
      refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkUpdating(false);
    }
  };

  const clearFilters = () => {
    setStatusFilter("");
    setTypeFilter("");
    setTeamFilter("");
    setSearch("");
  };

  const hasFilters = (statusFilter && statusFilter !== "all") || (typeFilter && typeFilter !== "all") || (teamFilter && teamFilter !== "all") || searchDebounced;

  return (
    <PharmeasyLayout user={user} onLogout={onLogout}>
      <div data-testid="escalation-management-page" className="space-y-6 p-4 md:p-6 max-w-[1400px] mx-auto">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-800">Escalation Management</h1>
            <p className="text-sm text-slate-500 mt-0.5">Upload, track, and resolve client escalations</p>
          </div>
          <Button data-testid="refresh-btn" variant="outline" size="sm" onClick={refreshAll} className="gap-1">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </div>

        <StatsCards stats={stats} />
        <UploadSection onUploadDone={refreshAll} />

        <Card>
          <CardHeader className="pb-4 pt-4 px-4">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex items-center gap-3 flex-1">
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-slate-400" />
                  <Input
                    data-testid="search-input"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search Order ID, LBN, remarks..."
                    className="pl-8 h-8 text-xs"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="status-filter" className="w-32 h-8 text-xs">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger data-testid="type-filter" className="w-32 h-8 text-xs">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {filterValues.types.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={teamFilter} onValueChange={setTeamFilter}>
                  <SelectTrigger data-testid="team-filter" className="w-32 h-8 text-xs">
                    <SelectValue placeholder="Team" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Teams</SelectItem>
                    {filterValues.teams.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {hasFilters && (
                  <Button data-testid="clear-filters-btn" variant="ghost" size="sm" onClick={clearFilters} className="text-xs h-8">
                    Clear
                  </Button>
                )}
              </div>
              <div className="text-xs text-slate-500">{total} results</div>
            </div>
          </CardHeader>

          {selected.size > 0 && (
            <div className="px-4 pb-2 flex items-center gap-3 bg-teal-50/50">
              <span className="text-xs text-teal-700 font-medium">{selected.size} selected</span>
              <Select value={bulkStatus} onValueChange={setBulkStatus}>
                <SelectTrigger data-testid="bulk-status-select" className="w-32 h-7 text-xs">
                  <SelectValue placeholder="Bulk status" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                data-testid="bulk-update-btn"
                size="sm"
                onClick={handleBulkUpdate}
                disabled={!bulkStatus || bulkUpdating}
                className="bg-teal-600 hover:bg-teal-700 h-7 text-xs"
              >
                {bulkUpdating ? <Loader2 className="h-3 w-3 animate-spin" /> : "Apply"}
              </Button>
            </div>
          )}

          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
              </div>
            ) : escalations.length === 0 ? (
              <div className="text-center py-16 text-slate-400">
                <FileSpreadsheet className="h-10 w-10 mx-auto mb-2 opacity-40" />
                <div className="text-sm">No escalations found</div>
                <div className="text-xs mt-1">Upload an Excel/CSV file to get started</div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50/50">
                      <th className="px-4 py-3 text-left w-8">
                        <Checkbox
                          data-testid="select-all-checkbox"
                          checked={selected.size === escalations.length && escalations.length > 0}
                          onCheckedChange={toggleAll}
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">ID</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Source ID</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Order ID</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">LBN</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Team</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Supplier</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">City</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Linked</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {escalations.map((esc) => (
                      <tr
                        key={esc.id}
                        data-testid={`escalation-row-${esc.id}`}
                        className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/pharmeasy/escalations/${esc.id}`)}
                      >
                        <td className="px-4 py-3 align-middle" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selected.has(esc.id)}
                            onCheckedChange={() => toggleSelect(esc.id)}
                          />
                        </td>
                        <td className="px-4 py-3 align-middle font-mono font-medium text-teal-700">{esc.id}</td>
                        <td className="px-4 py-3 align-middle text-slate-700 font-medium">{esc.source_id || "-"}</td>
                        <td className="px-4 py-3 align-middle font-medium text-slate-800 max-w-[120px] truncate">{esc.order_id || "-"}</td>
                        <td className="px-4 py-3 align-middle text-slate-600 max-w-[100px] truncate">{esc.lbn || "-"}</td>
                        <td className="px-4 py-3 align-middle"><StatusBadge status={esc.status} /></td>
                        <td className="px-4 py-3 align-middle text-slate-600 max-w-[100px] truncate">{esc.escalation_type || "-"}</td>
                        <td className="px-4 py-3 align-middle text-slate-600 max-w-[90px] truncate">{esc.escalation_team || "-"}</td>
                        <td className="px-4 py-3 align-middle text-slate-600 max-w-[80px] truncate">{esc.supplier || "-"}</td>
                        <td className="px-4 py-3 align-middle text-slate-600">{esc.city || "-"}</td>
                        <td className="px-4 py-3 align-middle">
                          {esc.linked_shipment ? (
                            <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200 text-xs">Linked</Badge>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 align-middle text-slate-500 whitespace-nowrap">{esc.opening_date || esc.created_at?.slice(0, 10) || "-"}</td>
                        <td className="px-4 py-3 align-middle">
                          <Button
                            data-testid={`view-detail-${esc.id}`}
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={(e) => { e.stopPropagation(); navigate(`/pharmeasy/escalations/${esc.id}`); }}
                          >
                            <Eye className="h-3.5 w-3.5 text-teal-600" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                <div className="text-xs text-slate-500">Page {page} of {totalPages}</div>
                <div className="flex gap-1">
                  <Button data-testid="prev-page-btn" variant="outline" size="sm" className="h-7 text-xs" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                    <ChevronLeft className="h-3 w-3" />
                  </Button>
                  <Button data-testid="next-page-btn" variant="outline" size="sm" className="h-7 text-xs" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                    <ChevronRight className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PharmeasyLayout>
  );
};

export default EscalationManagement;
